import React, { useState, useEffect, useMemo } from 'react';
import './SettingsPage.css';
import { useGetCompanySettingsMutation, useUpdateCompanySettingsMutation } from '../api/companyApi';
import ReactTable from '../../../components/ReactTable';
import Toast from '../../../components/ToastNotification';
import Loader from '../../../components/Loader';

const SettingsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    is_official: false,
    history_days: '',
    tags: ''
  });
  const [email, setEmail] = useState('');
  const [users, setUsers] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [getSettingsApi] = useGetCompanySettingsMutation();
  const [updateSettingsApi] = useUpdateCompanySettingsMutation();

  const fetchSettings = async () => {
    try {
      const response = await getSettingsApi().unwrap();
      const { company, settings, users: userRows } = response.data;

      setFormData({
        name: company.name || '',
        is_official: !!company.is_official,
        history_days: settings.history_days || '',
        tags: settings.tags || ''
      });
      setEmail(company.email || '');
      setUsers((userRows || []).map(u => ({
        ...u,
        daily_message_limit: String(u.daily_message_limit ?? '')
      })));
    } catch (error) {
      Toast.error(error?.data?.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleUserLimitChange = (userId, value) => {
    setUsers(prev => prev.map(u => (u.id === userId ? { ...u, daily_message_limit: value } : u)));
    setFieldErrors(prev => ({ ...prev, [`user_${userId}`]: '' }));
  };

  const columns = useMemo(() => [
    {
      header: 'USER',
      accessorKey: 'name'
    },
    {
      header: 'PHONE',
      id: 'phone',
      cell: ({ row }) => `${row.original.country_code || ''} ${row.original.phone_number || ''}`.trim()
    },
    {
      header: 'MESSAGE LIMIT (PER DAY)',
      id: 'daily_message_limit',
      cell: ({ row }) => {
        const user = row.original;
        const error = fieldErrors[`user_${user.id}`];
        return (
          <div className="limit-cell">
            <input
              type="number"
              min="1"
              className={`form-input limit-input ${error ? 'input-error' : ''}`}
              value={user.daily_message_limit}
              onChange={(e) => handleUserLimitChange(user.id, e.target.value)}
            />
            {error && <span className="error-text">{error}</span>}
          </div>
        );
      }
    }
  ], [fieldErrors]);

  const validate = () => {
    const errors = {};
    const name = formData.name.trim();
    const historyDays = Number(formData.history_days);

    if (!name) {
      errors.name = 'Company Name is required';
    } else if (name.length < 3) {
      errors.name = 'Company Name must be at least 3 characters';
    } else if (name.length > 100) {
      errors.name = 'Company Name must not exceed 100 characters';
    }

    if (formData.history_days === '') {
      errors.history_days = 'History Days is required';
    } else if (!Number.isInteger(historyDays) || historyDays < 1) {
      errors.history_days = 'History Days must be a whole number of at least 1';
    } else if (historyDays > 365) {
      errors.history_days = 'History Days must not exceed 365';
    }

    if (formData.tags.length > 1000) {
      errors.tags = 'Tags must not exceed 1000 characters';
    }

    for (const user of users) {
      const limit = Number(user.daily_message_limit);
      if (user.daily_message_limit === '' || !Number.isInteger(limit) || limit < 1) {
        errors[`user_${user.id}`] = 'Must be a whole number of at least 1';
      } else if (limit > 100000) {
        errors[`user_${user.id}`] = 'Must not exceed 100000';
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      Toast.error('Please fix the highlighted fields before saving.');
      return;
    }

    setSaving(true);
    try {
      const tags = formData.tags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
        .join(',');

      const response = await updateSettingsApi({
        company: {
          name: formData.name.trim(),
          is_official: formData.is_official
        },
        settings: {
          history_days: Number(formData.history_days),
          tags
        },
        users: users.map(u => ({
          id: u.id,
          daily_message_limit: Number(u.daily_message_limit)
        }))
      }).unwrap();

      if (response.success) {
        Toast.success(response.message || 'Settings updated successfully.');
        fetchSettings();
      }
    } catch (error) {
      Toast.error(error?.data?.message || 'Failed to update settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="content-wrapper settings-page">
      <form onSubmit={handleSubmit} noValidate>

        <div className="settings-card">
          <div className="layout-header d-flex">
            <h2 className="page-title">Settings</h2>
            <button type="submit" className="add-new-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        <div className="settings-card">
          <h3 className="settings-section-title">Company Information</h3>
          <div className="settings-grid">
            <div className="form-group">
              <label htmlFor="name">Company Name</label>
              <input
                id="name"
                type="text"
                name="name"
                className={`form-input ${fieldErrors.name ? 'input-error' : ''}`}
                placeholder="Enter company name"
                value={formData.name}
                onChange={handleChange}
              />
              {fieldErrors.name && <span className="error-text">{fieldErrors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="form-input"
                value={email}
                disabled
              />
              <span className="hint-text">Login email cannot be changed.</span>
            </div>

            <div className="form-group">
              <label htmlFor="is_official">Official (Meta API)</label>
              <label className="checkbox-row">
                <input
                  id="is_official"
                  type="checkbox"
                  name="is_official"
                  checked={formData.is_official}
                  onChange={handleChange}
                />
                <span>{formData.is_official ? 'Official (Meta API)' : 'Unofficial (Baileys)'}</span>
              </label>
              <span className="hint-text">
                When enabled, messages are routed through the official Meta WhatsApp API if Baileys is not connected or fails to send the message.
              </span>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3 className="settings-section-title">Messaging Configuration</h3>
          <div className="settings-grid">
            <div className="form-group">
              <label htmlFor="history_days">History Days</label>
              <input
                id="history_days"
                type="number"
                name="history_days"
                min="1"
                max="365"
                className={`form-input ${fieldErrors.history_days ? 'input-error' : ''}`}
                placeholder="Enter history days"
                value={formData.history_days}
                onChange={handleChange}
              />
              {fieldErrors.history_days && <span className="error-text">{fieldErrors.history_days}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags</label>
              <input
                id="tags"
                type="text"
                name="tags"
                className={`form-input ${fieldErrors.tags ? 'input-error' : ''}`}
                placeholder="Comma separated, e.g. ok, bye, thanks"
                value={formData.tags}
                onChange={handleChange}
              />
              <span className="hint-text">Separate multiple tags with commas.</span>
              {fieldErrors.tags && <span className="error-text">{fieldErrors.tags}</span>}
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3 className="settings-section-title">Per-User Message Limits</h3>
          <div className="layout-body">
            <ReactTable columns={columns} data={users} />
            {users.length === 0 && (
              <p className="hint-text">No users found for this company.</p>
            )}
          </div>
        </div>

      </form>
    </div>
  );
};

export default SettingsPage;
